import React, { useCallback, useMemo } from 'react';
import { useQueries } from '@tanstack/react-query';
import {
  Button, Heading, Container,
} from '@printdeal/ui-library';
import { Link } from 'gatsby';
import { FormattedMessage, useIntl } from 'react-intl';
import { InView } from 'react-intersection-observer';
import { addDataLayerEntry } from '@printdeal/data-layer-manager';
import {
  recentOrderEvents,
  getOrderIdList, recentOrderMessages, handleAllOrdersClick,
  getOrderAge,
  getLineItemData,
} from './RecentOrderHelper';
import Order, { SanitizedLineItem, SanitizedOrder } from '../../../types/order/Order';
import useSystemPages from '../../../hooks/useSystemPages';
import { ServiceFactory } from '../../../services/ServiceFactory';
import { sanitizeOrder } from '../../my-account/helpers/order-helper';
import { RecentOrderItemPanel } from './RecentOrderItemPanel';

export interface RecentOrderProps {
  orders: Order[];
}

interface OrderLineItem {
  orderNumber: string,
  lineItemId: string,
}

const RecentOrder = ({ orders }: RecentOrderProps) => {
  const { locale } = useIntl();
  const [accountDashboardPage] = useSystemPages('accountDashboard');
  const orderLineitemsList = useMemo(
    () => getOrderIdList(orders ?? []),
    [orders],
  );

  const queryResults = useQueries({
    queries: orderLineitemsList.map((orderLineItem) => ({
      queryKey: ['recent-orders-home', orderLineItem.orderNumber],
      queryFn: async () => {
        const orderQuery = await ServiceFactory.createOrderService()
          .getOrder(orderLineItem.orderNumber);
        return {
          sanitisedOrder: sanitizeOrder(orderQuery, locale, false),
          orderLineItem,
        };
      },
    })),
  }).map((result) => ({
    ...result,
    lineItem: getLineItemData(result?.data?.sanitisedOrder, result?.data?.orderLineItem as OrderLineItem),
  }));

  const handleRecentOrderPanelInView = useCallback((inView: boolean) => {
    // Only dispatch this event once.
    if (inView) {
      const inViewData = queryResults.map((result, index) => {
        if (!result) return {};
        const data: any = {};
        data[`position_${index + 1}_status`] = result.data?.orderLineItem.lineItemStatus;
        data[`orderAge${index + 1}`] = getOrderAge(new Date(queryResults[1]?.lineItem?.sane.finalDeliveryDate));
        return data;
      });

      addDataLayerEntry(
        recentOrderEvents.RECENT_ORDER_VIEW,
        Object.assign({}, ...inViewData),
      );
    }
  }, [queryResults]);

  if (!orders || orders.length === 0) {
    return null;
  }

  if (!orderLineitemsList) {
    return null;
  }

  return (
    <>
      {(queryResults.every((result) => result.isSuccess)) && (
        <InView onChange={handleRecentOrderPanelInView} key={orders[0].id} threshold={1} triggerOnce>
          {({ ref }) => (
            <Container size="md" className="pd-relative pd-flex pd-h-full pd-flex-col pd-gap-5 pd-p-0" ref={ref}>
              <Heading as="h2">
                <FormattedMessage
                  id={recentOrderMessages.recentOrderSectionHeading.id}
                  defaultMessage={recentOrderMessages.recentOrderSectionHeading.defaultMessage}
                />
              </Heading>
              <div className="pd-flex pd-flex-col pd-gap-6">
                <div className="pd-flex pd-flex-col pd-gap-6 md:pd-min-h-[26rem]">
                  {queryResults.map((result, index) => (
                    <RecentOrderItemPanel
                      lineItem={result.lineItem as SanitizedLineItem}
                      order={result.data?.sanitisedOrder as SanitizedOrder}
                      position={(index + 1).toString()}
                      key={result.data?.orderLineItem?.lineItemId}
                    />
                  ))}
                </div>
                <Button
                  as={Link}
                  to={`${accountDashboardPage.path}/orders`}
                  variant="tertiary"
                  iconBefore="chevron-right"
                  onClick={handleAllOrdersClick}
                  className="pd-w-full"
                >
                  <FormattedMessage
                    id={recentOrderMessages.recentOrderAllOrdersButton.id}
                    defaultMessage={recentOrderMessages.recentOrderAllOrdersButton.defaultMessage}
                  />
                </Button>
              </div>
            </Container>
          )}
        </InView>
      )}
    </>
  );
};

export default RecentOrder;
